
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import { handleGlobalError, logError } from './lib/error-handler';

// Add extensive debug info to help diagnose rendering issues
console.log('Main.tsx executing - application initialization started');
console.log('Environment check:', {
  env: import.meta.env.MODE,
  base: import.meta.env.BASE_URL,
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? 'Defined' : 'Undefined',
  supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Defined' : 'Undefined'
});

// Register global error handler with improved logging
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
  handleGlobalError(event);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled rejection:', event.reason);
  logError(event.reason, 'UnhandledRejection');
});

// Get root element and confirm it exists
const rootElement = document.getElementById("root");
if (!rootElement) {
  console.error("Root element not found! Creating one...");
  const newRoot = document.createElement("div");
  newRoot.id = "root";
  document.body.appendChild(newRoot);
}

// Create and render root with enhanced error handling
try {
  console.log('Creating React root...');
  const root = createRoot(rootElement || document.body);
  
  root.render(
    <ErrorBoundary fallback={
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="p-6 max-w-sm mx-auto bg-card rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4">Application Error</h2>
          <p className="text-muted-foreground mb-4">
            Sorry, something went wrong. Please try refreshing the page.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    }>
      <App />
    </ErrorBoundary>
  );
  console.log('App mounted successfully');
} catch (error) {
  console.error('Critical error during app initialization:', error);
  // Create a more user-friendly error display
  const errorDisplay = document.createElement('div');
  errorDisplay.innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; background: #f8fafc;">
      <div style="max-width: 500px; padding: 24px; background: white; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
        <h1 style="font-size: 24px; font-weight: bold; margin-bottom: 16px; color: #1e293b;">Application Error</h1>
        <p style="color: #64748b; margin-bottom: 16px;">The application failed to initialize. This might be due to:</p>
        <ul style="list-style: disc; margin-left: 20px; color: #64748b; margin-bottom: 16px;">
          <li>Missing environment variables</li>
          <li>Network connectivity issues</li>
          <li>JavaScript runtime errors</li>
        </ul>
        <p style="color: #64748b; margin-bottom: 16px;">Please try:</p>
        <ol style="list-style: decimal; margin-left: 20px; color: #64748b;">
          <li>Refreshing the page</li>
          <li>Clearing your browser cache</li>
          <li>Checking your internet connection</li>
        </ol>
        <button onclick="window.location.reload()" 
                style="margin-top: 20px; padding: 8px 16px; background: #0ea5e9; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(errorDisplay);
}
